---
title: 'Commerce Hub API (CHAPI)'
metaTitle: 'Admin-UI Docs | Getting started > Commerce Hub API (CHAPI)'
metaDescription: 'How to set up and run the Commerce Hub API (CHAPI) with backend and frontend'
---

The Commerce Hub uses a **feature stack** architecture that includes a local backend service alongside the frontend. This guide explains how to set up and run the complete stack.

## Quick Start

The Commerce Hub uses the monorepo [start script](/getting-started/02-install-and-launch-project) to run both the backend and frontend together.

First, create a `start.config.ts` file at the repository root (this file is gitignored and personal to each developer):

```typescript
import {defineConfig} from '@helpers/start-script';

export default defineConfig({
    defaultConfig: 'commerce-hub',
    configs: {
        'commerce-hub': {
            noPrompt: true,
            smartBuild: true,
            packages: [
                '@apps/commerce-hub',
                '@backends/commerce-hub',
                '@configurations/commerce-hub',
                '@pages/commerce-hub',
                '@pages/commerce-health',
            ],
        },
        'commerce-hub-fe': {
            noPrompt: true,
            smartBuild: true,
            packages: [
                '@apps/commerce-hub',
                '@configurations/commerce-hub',
                '@pages/commerce-hub',
            ],
        },
    },
});
```

Then simply run:

```bash
pnpm start
```

This starts:

- **Backend** (`@backends/commerce-hub`): Express server on `http://localhost:8001`
- **Frontend** (`@apps/commerce-hub`): Vite dev server on `https://localhost:9001`
- **Pages** (`@pages/commerce-hub`, `@pages/commerce-health`)
- **Configuration** (`@configurations/commerce-hub`)

> **Tip**: If you only need the frontend (no backend), use the frontend-only config: `pnpm start --config commerce-hub-fe`

## Prerequisites

Before running CHAPI, ensure you have:

1. **VPN Connection**: You must be connected to the Coveo VPN
2. **AWS SSO Authentication**: Your AWS SSO authentication must be valid
3. **Local MySQL Database**: Required for backend development
4. **Node.js & pnpm**: Make sure you have the required versions installed

### Setting up Local MySQL

A local MySQL database is required for backend development:

#### macOS (Homebrew)

1. Install MySQL locally:

    ```bash
    brew install mysql
    brew services start mysql
    ```

#### Windows

1. Download and install MySQL from the [official website](https://dev.mysql.com/downloads/installer/)
2. During installation, choose "MySQL Server" and "MySQL Shell"
3. Set root password to empty (or remember it for connection string)
4. Start MySQL service:

    ```powershell
    # Via Services app or command line
    net start mysql
    ```

#### All Platforms - Run Migrations

Run migrations (this will also create the database if it doesn't exist):

```bash
pnpm --filter @backends/commerce-hub migrate:local
```

## Installation

From the root of the repository:

```bash
# Install all dependencies
pnpm install
```

## Running CHAPI

### Full Stack (Recommended)

The start script handles building dependencies, starting all required packages, and setting the `FEATURE_STACK=true` environment variable automatically when a `@backends/` package is selected.

```bash
pnpm start
```

### Frontend Only


```bash
pnpm start --config commerce-hub-fe
```

### Interactive Package Selection

You can also select packages interactively with fuzzy search:

```bash
pnpm start --no-config
```

See the [start script documentation](/getting-started/03-command-line-interface) for all available options.

## Database

This project uses [Prisma](https://www.prisma.io/) for the ORM (Object-Relational Mapping) to interact with the MySQL database. Prisma provides type-safe database access and automatic migrations.

### What is Prisma?

Prisma simplifies database operations by managing:

- **Type-safe queries**: Auto-generated TypeScript types based on your schema
- **Migrations**: Version-controlled database schema changes
- **Prisma Client**: Auto-generated query builder for your database

### Schema

The database schema is defined in `prisma/schema.prisma`. When you modify this file, you need to create a migration and regenerate the Prisma Client.

### Migration Scripts

Prisma provides several migration commands for different scenarios:

#### `migrate:local` (prisma migrate dev)

This is the primary command for development. It:

1. **Detects schema drift** - Compares your schema against the shadow database
2. **Applies pending migrations** - Runs any migrations not yet applied
3. **Generates new migration** - Creates a migration file from schema changes
4. **Updates the database** - Applies changes and updates `_prisma_migrations` table
5. **Regenerates Prisma Client** - Updates TypeScript types automatically

```bash
pnpm --filter @backends/commerce-hub migrate:local
```

When prompted, provide a descriptive name for your migration (e.g., `add_user_preferences_table`).

#### `migrate:reset` (prisma migrate reset)

⚠️ **Use only in development** - This destroys all data!

Useful when:

- You need a clean slate
- Migration history is corrupted
- You want to test migrations from scratch

```bash
pnpm --filter @backends/commerce-hub migrate:reset
```

This command:

1. Drops the database/schema
2. Recreates an empty database
3. Applies all migrations from scratch
4. Runs seed scripts (if configured)

#### `migrate:status` (prisma migrate status)

Check the current state of your migrations:

```bash
pnpm --filter @backends/commerce-hub migrate:status
```

This shows:

- Number of migrations in `prisma/migrations/`
- Which migrations have been applied
- Any pending or missing migrations
- Schema drift detection

### Migration Best Practices

1. **Always review generated migrations** before committing them
2. **Use descriptive migration names** (e.g., `add_index_on_organization_id`)
3. **Never edit applied migrations if they have already been merged to dev** Create a new migration instead
4. **When applying reviews to a PR** delete the old migration file from the branch and generate a new one. Your reviewers will thank you
5. **Test migrations locally** before pushing to CI/CD

### Usage Example

```typescript
// Access Prisma Client from the shared context
const {prisma} = sharedContext;

// Query examples
const allRules = await prisma.ruleSettings.findMany();
const rule = await prisma.ruleSettings.findUnique({
    where: {organizationId_ruleId: {organizationId: 'org-123', ruleId: 'rule-456'}},
});

// Create a new record
await prisma.ruleSettings.create({
    data: {organizationId: 'org-123', ruleId: 'rule-456', muted: false},
});
```

## Troubleshooting

### Could not mount static UI routes

This warning appears when running locally without a built frontend bundle. This is expected when running CHAPI.

**Solution**: Access the frontend at `https://localhost:9001/?dev` instead, or run the full stack with `pnpm start` which handles building automatically.

### VPN Connection Required

Most backend functionality requires VPN access to reach AWS services.

**Solution**: Connect to the Coveo VPN before starting the backend.

### AWS SSO Expired

If you get authentication errors, your AWS SSO session may have expired.

**Solution**:

```bash
aws sso login --profile <your-profile>
```

### Port Already in Use

If port 8001 or 9001 is already in use:

#### macOS

```bash
# Find the process
lsof -i :8001

# Kill the process
kill -9 <PID>
```

#### Windows

```powershell
# Find the process
netstat -ano | findstr :8001

# Kill the process (replace <PID> with the actual PID from above)
taskkill /PID <PID> /F
```

### Database Connection Issues

#### macOS (Homebrew)

```bash
# Check MySQL is running
brew services list | grep mysql

# Restart MySQL
brew services restart mysql
```

#### Windows

```powershell
# Check MySQL service status
Get-Service mysql*

# Restart MySQL service
Restart-Service mysql
# Or via net command
net stop mysql && net start mysql
```

### Logs

The backend outputs logs to the console. Set `LOG_LEVEL` for more verbose output:

```bash
LOG_LEVEL=debug pnpm start
```

### PR Demo Environment Troubleshooting

PR demos for Commerce Hub are deployed to Kubernetes. Use `kubectl` with context `dev-infra-us-east-1-commerce-hub-admin` and namespace `commerce-hub` (authenticate via StrongDM (SDM)).

### Demo Not Reflecting Latest Changes

If the PR demo link shows stale content after a successful deployment:

1. Check the pod status:

    ```bash
    kubectl --context dev-infra-us-east-1-commerce-hub-admin -n commerce-hub get pods | grep <PR_NUMBER>
    ```

2. If the pod is in a failed state (e.g., `Error`, `CrashLoopBackOff`), delete it:

    ```bash
    kubectl --context dev-infra-us-east-1-commerce-hub-admin -n commerce-hub delete pod <pod-name>
    ```

3. Kubernetes will automatically create a new pod with the latest build.

**Common causes**: MySQL sidecar failing to start, corrupted pod state, or resource issues.
